import axios from 'axios';
import { delay } from 'redux-saga';
import { select, call, put, race, take } from 'redux-saga/effects';

import { REFRESH_TOKEN_TIMEOUT_IN_MS } from '../../constants';
import { makeSelectAccessToken } from '../auth/selectors';
import { REFRESH_SUCCESS, REFRESH_FAILURE } from '../auth/types';
import { requestRefreshToken, logout } from '../auth/reducer';

const client = axios.create();

const AuthApiClient = {};

function* addAuthorization(options = {}) {
  const token = yield select(makeSelectAccessToken());

  if (!token) {
    return options;
  }

  const { publicRequest, marketplaceToken, headers, ...rest } = options;

  const authorizationHeader = publicRequest
    ? {}
    : { Authorization: `Bearer ${marketplaceToken ?? token}` };

  return {
    responseType: 'json',
    ...rest,
    headers: {
      ...headers,
      ...authorizationHeader,
    },
  };
}

/**
 * @name handleRefresh
 * @description
 * Method that handles the refresh token process. It triggers the refresh token flow
 * and waits for the success/failure events or a timeout. If the refresh is successful
 * it will redo the original request with the new header.
 */
function* handleRefresh(originalRequest) {
  // Request refresh token
  yield put(requestRefreshToken());

  // Wait for either success, failure or timeout
  const { failure, timeout } = yield race({
    success: take(REFRESH_SUCCESS),
    failure: take(REFRESH_FAILURE),
    timeout: call(delay, REFRESH_TOKEN_TIMEOUT_IN_MS),
  });

  // If the refresh failed or timeout we throw an error
  if (failure || timeout) {
    throw new Error('Refresh token failed');
  }

  // Update headers with the new token
  const updatedOptions = yield call(addAuthorization, originalRequest);

  // Redo the original request with the new token
  return yield call(client.request, updatedOptions);
}

AuthApiClient.request = function* request(options) {
  const redirect = options?.data?.redirect;
  const optionsWithoutRedirect = options;
  delete optionsWithoutRedirect?.data?.redirect;
  // Update the received options with a fresh access token from the store
  const updatedOptions = yield call(addAuthorization, optionsWithoutRedirect);

  try {
    return yield call(client.request, updatedOptions);
  } catch (error) {
    // If a 401 error occurred we try to refresh and redo the request
    if (error.response && error.response.status === 401) {
      try {
        return yield call(handleRefresh, { ...optionsWithoutRedirect });
      } catch (e) {
        if (redirect !== false) {
          // If the refresh process failed
          // and it's to redirect, we kick the user out
          // and propagate the original error
          yield put(logout());
        }
        throw error;
      }
    }

    throw error;
  }
};

AuthApiClient.get = function* getRequest(url, options = {}) {
  return yield AuthApiClient.request({
    url,
    method: 'get',
    ...options,
  });
};

AuthApiClient.post = function* postRequest(url, data, options = {}) {
  return yield AuthApiClient.request({
    url,
    method: 'post',
    data,
    ...options,
  });
};

AuthApiClient.put = function* putRequest(url, data, options = {}) {
  return yield AuthApiClient.request({
    url,
    method: 'put',
    data,
    ...options,
  });
};

AuthApiClient.patch = function* patchRequest(url, data, options = {}) {
  return yield AuthApiClient.request({
    url,
    method: 'patch',
    data,
    ...options,
  });
};

AuthApiClient.delete = function* deleteRequest(url, options = {}) {
  return yield AuthApiClient.request({
    url,
    method: 'delete',
    ...options,
  });
};

AuthApiClient.client = client;

export default AuthApiClient;
